/* TAB 1 — NẠP DỮ LIỆU
   Chọn thư mục (File System Access, nhớ qua IndexedDB) hoặc chọn file / kéo thả
   → parse Excel trên trình duyệt (src/core) → mỗi file MỚI gọi upsert_orders(file, hash)
   → tải lại RAM → render tổng quan + cảnh báo như v4.
   File nào đã có hash trong kho.imports thì báo "đã nạp", không gửi lại. */
import * as XLSX from 'xlsx';
import * as K from '../core/index.js';
import { S, loadActive, totalCounts, setSync } from '../store.js';
import { sb, rpc, sha256 } from '../lib/supabase.js';
import { $, esc, fmt, log, dialog, kvSet, kvGet } from '../lib/ui.js';

const T = { dir: null, pending: null, files: [], imported: {}, readyShown: false };
let WARN = null;
const HAS_FS = 'showDirectoryPicker' in window;

export function initTab1(goPick) {
  if (!HAS_FS) {
    $('#fsNote').textContent = 'trình duyệt này không đọc được thư mục — dùng nút Chọn file';
    $('#btnDir').disabled = true;
  }
  $('#btnDir').onclick = pickDir;
  $('#btnFiles').onclick = () => $('#fileInput').click();
  $('#fileInput').onchange = (e) => addFiles(Array.from(e.target.files || []));
  const dz = $('#dropzone');
  dz.ondragover = (e) => { e.preventDefault(); dz.classList.add('over'); };
  dz.ondragleave = () => dz.classList.remove('over');
  dz.ondrop = (e) => { e.preventDefault(); dz.classList.remove('over'); addFiles(Array.from(e.dataTransfer.files || [])); };
  $('#btnRun').onclick = run;
  $('#btnGoPick').onclick = goPick;
  T.goPick = goPick;
}

/* ---------- thư mục ---------- */
async function pickDir() {
  try {
    if (T.pending) {
      let ok = false;
      try { ok = (await T.pending.requestPermission({ mode: 'read' })) === 'granted'; } catch (e) { }
      if (ok) { T.dir = T.pending; T.pending = null; await afterDir(); return; }
      log('Thư mục đã nhớ không cấp được quyền, chọn lại thủ công.', 'wr');
    }
    const h = await window.showDirectoryPicker({ mode: 'read' });
    await kvSet('dirHandle', h);
    T.dir = h; T.pending = null; await afterDir();
  } catch (e) { if (e.name !== 'AbortError') log('Lỗi chọn thư mục: ' + e.message, 'er'); }
}
async function afterDir() {
  $('#dirDot').classList.add('on');
  $('#dirLbl').textContent = T.dir.name;
  $('#btnDir').textContent = 'Đổi thư mục';
  $('#dirSub').textContent = 'đã kết nối';
  $('#dirHint').style.display = 'none';
  log('Thư mục làm việc: ' + T.dir.name, 'ok');
  await scanDir();
}
async function scanDir() {
  if (!T.dir) return;
  const list = []; let skipped = 0;
  for await (const ent of T.dir.values()) {
    if (ent.kind !== 'file') continue;
    const n = ent.name;
    if (n.startsWith('~$') || n.startsWith('.')) { skipped++; continue; }
    if (!/\.(xlsx|xls)$/i.test(n)) continue;
    list.push(await ent.getFile());
  }
  log('Quét thấy ' + list.length + ' file Excel' + (skipped ? ' (' + skipped + ' file tạm bị bỏ qua)' : ''), list.length ? 'ok' : 'wr');
  T.files = [];
  await addFiles(list);
}

/* Khôi phục thư mục đã nhớ — chỉ queryPermission, KHÔNG requestPermission lúc tải trang */
export async function restoreDir() {
  if (!HAS_FS) return;
  try {
    const h = await kvGet('dirHandle'); if (!h) return;
    let st = 'prompt';
    try { st = await h.queryPermission({ mode: 'read' }); } catch (e) { }
    if (st === 'granted') { T.dir = h; await afterDir(); return; }
    T.pending = h;
    $('#dirLbl').textContent = h.name + ' · cần cấp quyền';
    $('#btnDir').textContent = 'Mở lại thư mục làm việc';
    $('#dirSub').textContent = 'đã nhớ từ lần trước';
    log('Đã nhớ thư mục từ lần trước. Bấm "Mở lại thư mục làm việc" để cấp quyền.', 'wr');
  } catch (e) { log('Không đọc được thư mục đã nhớ: ' + e.message, 'wr'); }
}

/* ---------- danh sách file + trạng thái đã nạp ---------- */
async function addFiles(files) {
  const xl = files.filter((f) => /\.(xlsx|xls)$/i.test(f.name) && !f.name.startsWith('~$'));
  for (const f of xl) {
    if (T.files.some((x) => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified)) continue;
    const buf = await f.arrayBuffer();
    T.files.push({ name: f.name, size: f.size, lastModified: f.lastModified, buf, hash: await sha256(buf), channel: null, rows: null, status: 'new' });
  }
  T.files.sort((a, b) => a.lastModified - b.lastModified);
  await markImported();
  renderFiles();
  readyUI();
}
async function markImported() {
  if (!T.files.length) return;
  try {
    const { data, error } = await sb.from('imports').select('file_hash,file_name,imported_at,may')
      .in('file_hash', T.files.map((f) => f.hash));
    if (error) throw error;
    T.imported = {};
    (data || []).forEach((r) => { T.imported[r.file_hash] = r; });
    T.files.forEach((f) => { f.status = T.imported[f.hash] ? 'done' : 'new'; });
  } catch (e) { log('Không kiểm tra được file đã nạp: ' + e.message, 'wr'); }
}
function newFiles() { return T.files.filter((f) => f.status === 'new'); }

function renderFiles() {
  const box = $('#fileList'); box.innerHTML = '';
  $('#rowSetup').style.display = T.files.length ? '' : 'none';
  $('#rowData').style.display = (T.files.length || hasAny()) ? '' : 'none';
  $('#cardFiles').style.display = T.files.length ? '' : 'none';
  $('#filesNote2').textContent = T.files.length ? T.files.length + ' file' : '';
  T.files.forEach((f) => {
    const d = document.createElement('div');
    d.className = 'file ' + (f.channel || ''); d.dataset.hash = f.hash;
    const st = f.status === 'done' ? '<span class="st done" title="' + esc(imp(f)) + '">đã nạp</span>'
      : f.status === 'err' ? '<span class="st err">lỗi</span>' : '<span class="st new">mới</span>';
    d.innerHTML = '<span class="chan ' + (f.channel || 'NONE') + '">' + (f.channel || '?') + '</span>' +
      '<div class="nm">' + esc(f.name) + '</div>' + st +
      '<div class="num">' + (f.rows != null ? '<b>' + fmt(f.rows) + '</b> dòng' : (f.size / 1024).toFixed(0) + ' KB') + '</div>';
    box.appendChild(d);
  });
  $('#btnRun').disabled = !newFiles().length;
}
function imp(f) {
  const r = T.imported[f.hash]; if (!r) return '';
  return 'nạp lúc ' + new Date(r.imported_at).toLocaleString('vi-VN') + (r.may ? ' bởi ' + r.may : '');
}
function hasAny() { return Object.keys(S.orders).length > 0 || Object.keys(S.counts).length > 0; }

/* ---------- xử lý ---------- */
async function run() {
  const btn = $('#btnRun'); btn.disabled = true; btn.textContent = 'Đang xử lý…';
  const rp = { files: 0, added: 0, statusChanged: 0, cancelled: [], itemChanged: [], lostQty: 0, purged: 0, skipped: 0, total: 0 };
  const ds = { orders: {}, duplicates: [], unknownStatuses: {}, noBarcode: [], files: [] };
  try {
    const todo = newFiles();
    if (!todo.length) { log('Không có file mới để nạp.', 'wr'); return; }
    const parsed = [];
    for (const f of todo) {
      const pf = K.parseBuffer(XLSX, new Uint8Array(f.buf), f.name, f.lastModified);
      f.channel = pf.channel; f.rows = pf.rows.length;
      if (!pf.channel) { f.status = 'err'; log('Không nhận diện được cột đơn hàng: ' + f.name, 'er'); continue; }
      parsed.push({ f, pf });
      log(pf.channel + ' · ' + f.name + ' · ' + pf.rows.length + ' dòng', 'ok');
    }
    renderFiles();
    if (!parsed.length) { log('Không file nào đọc được.', 'er'); return; }

    // Cảnh báo trùng / thiếu mã vạch / trạng thái lạ: tính trên tập file vừa nạp (như v4)
    const all = K.buildDataset(parsed.map((x) => x.pf));
    ds.duplicates = all.duplicates; ds.unknownStatuses = all.unknownStatuses; ds.noBarcode = all.noBarcode;

    // Gửi từng file theo thứ tự cũ → mới: file mới ghi đè, cùng quy tắc buildDataset
    setSync('busy', 'đang gửi ' + parsed.length + ' file…');
    for (const { f, pf } of parsed) {
      const one = K.buildDataset([pf]);
      const arr = Object.values(one.orders);
      const t0 = performance.now();
      const { data } = await rpc('upsert_orders', { p_orders: arr, p_file: f.name, p_hash: f.hash });
      const ms = Math.round(performance.now() - t0);
      if (data.skipped) { rp.skipped++; f.status = 'done'; log('Đã nạp trước đó, bỏ qua: ' + f.name, 'wr'); continue; }
      rp.files++; rp.added += data.added; rp.statusChanged += data.statusChanged; rp.lostQty += data.lostQty;
      rp.purged += data.purged; rp.total += data.total;
      rp.cancelled = rp.cancelled.concat(data.cancelled || []);
      rp.itemChanged = rp.itemChanged.concat(data.itemChanged || []);
      f.status = 'done';
      log('Máy chủ nhận ' + f.name + ': +' + data.added + ' mới, ' + data.statusChanged + ' đổi trạng thái (' + ms + ' ms)', 'ok');
    }
    await markImported();
    renderFiles();
    await loadActive();
    render(ds);
    mergeReport(rp);
    readyUI();
    if (T.onData) T.onData();
  } catch (e) {
    log('Lỗi nạp: ' + e.message, 'er'); console.error(e);
    $('#mergeBox').innerHTML = '<div class="note e"><span class="ic">!</span><div><b>Nạp thất bại</b> — ' + esc(e.message) + '. Dữ liệu trên máy chủ không bị thay đổi nửa chừng (mỗi file là một giao dịch).</div></div>';
    setSync('off', 'lỗi');
  } finally {
    btn.disabled = !newFiles().length; btn.textContent = 'Xử lý dữ liệu';
  }
}

function mergeReport(rp) {
  const tc = totalCounts();
  const h = [];
  h.push('<div class="note s"><span class="ic">✓</span><div><b>Đã nạp ' + rp.files + ' file</b> — thêm <b>' + rp.added +
    '</b> đơn mới, cập nhật trạng thái <b>' + rp.statusChanged + '</b> đơn' +
    (rp.purged ? ', chuyển lưu trữ ' + rp.purged + ' đơn quá ' + K.PURGE_DAYS + ' ngày' : '') +
    (rp.skipped ? ', bỏ qua ' + rp.skipped + ' file đã nạp' : '') +
    '. Máy chủ hiện có <b>' + fmt(tc.total) + '</b> đơn · <b>' + fmt(S.sum.byGroup.CAN_GIAO) + '</b> cần giao. Kết quả pick và bàn giao cũ được giữ nguyên.</div></div>');
  if (rp.cancelled.length) {
    h.push('<div class="note e"><span class="ic">!</span><div><b>' + rp.cancelled.length +
      ' đơn đã xử lý nhưng sàn đã hủy</b> — cần chặn lại trước khi giao:<ul style="margin:8px 0 0;padding-left:20px">' +
      rp.cancelled.map((c) => '<li><b>' + esc(c.id) + '</b> (' + esc(c.chan) + ') · ' + esc(c.tr || '—') + ' — ' + (c.out ? 'ĐÃ BÀN GIAO' : 'đã nhặt') + '</li>').join('') + '</ul></div></div>');
  }
  if (rp.itemChanged.length) {
    h.push('<div class="note w"><span class="ic">!</span><div><b>' + rp.itemChanged.length +
      ' đơn đã nhặt có danh sách sản phẩm thay đổi</b>' +
      (rp.lostQty ? ' — <b>' + rp.lostQty + '</b> sản phẩm đã nhặt không còn khớp dòng nào' : '') +
      '. Mở lại các đơn này ở tab Pick kiểm tra bằng mắt:<ul style="margin:8px 0 0;padding-left:20px">' +
      rp.itemChanged.map((c) => '<li><b>' + esc(c.id) + '</b> (' + esc(c.chan) + ')</li>').join('') + '</ul></div></div>');
  }
  $('#mergeBox').innerHTML = h.join('');
  log('Xử lý dữ liệu: +' + rp.added + ' đơn mới · ' + rp.statusChanged + ' đổi trạng thái · máy chủ có ' + tc.total + ' đơn.', 'ok');
  if (rp.cancelled.length) log('CẢNH BÁO: ' + rp.cancelled.length + ' đơn đã xử lý nhưng sàn đã hủy.', 'er');
  if (rp.itemChanged.length) log(rp.itemChanged.length + ' đơn đã nhặt bị đổi danh sách sản phẩm — cần kiểm tra lại.', 'wr');
}

/* ---------- tổng quan (từ RAM + số đếm máy chủ) ---------- */
export function render(ds) {
  if (!hasAny()) return;
  $('#rowSetup').style.display = ''; $('#rowData').style.display = ''; $('#cardStat').style.display = '';
  const s = S.sum, tc = totalCounts();
  $('#statNote').textContent = 'máy chủ · ' + (S.loadedAt ? S.loadedAt.toLocaleTimeString('vi-VN') : '');
  const st = [
    ['Tổng đơn', fmt(tc.total), '', 'trên máy chủ'],
    ['Cần giao', fmt(s.byGroup.CAN_GIAO), 'ok', 'sẵn sàng pick'],
    ['Đơn mới', fmt(s.byGroup.MOI), 'info', s.moiThieuMVD + ' chưa có mã vận đơn'],
    ['Đã xong', fmt(tc.byGroup.XONG), '', ''],
    ['Đã hủy', fmt(tc.byGroup.HUY), 'bad', ''],
    ['Dòng SP', fmt(s.items), '', fmt(s.units) + ' sản phẩm đang xử lý']
  ];
  $('#statBox').innerHTML = st.map((x) => '<div class="stat"><div class="k">' + x[0] + '</div><div class="v ' + x[2] + '">' + x[1] + '</div><div class="s">' + x[3] + '</div></div>').join('');

  const order = ['CAN_GIAO', 'MOI', 'XONG', 'HUY', 'KHAC'];
  let bar = '', leg = '';
  order.forEach((g) => {
    const n = tc.byGroup[g] || 0; if (!n) return;
    const pc = n / tc.total * 100;
    bar += '<div class="g-' + g + '" style="width:' + pc + '%" title="' + K.GROUPS[g] + ': ' + n + '">' + (pc > 7 ? n : '') + '</div>';
    leg += '<span><i style="background:' + segColor(g) + '"></i>' + K.GROUPS[g] + ' · ' + n + '</span>';
  });
  $('#segBar').innerHTML = bar; $('#segLeg').innerHTML = leg;

  const rows = ['<tr><th>Kênh</th><th>Cần giao</th><th>Đơn mới</th><th>Đã xong</th><th>Đã hủy</th><th>Tổng</th></tr>'];
  ['SHOPEE', 'TIKTOK'].forEach((c) => {
    if (!tc.byChannel[c]) return;
    const g = (x) => tc.matrix[c + '|' + x] || 0;
    rows.push('<tr><td><span class="chan ' + c + '">' + c + '</span></td>' +
      '<td class="n">' + fmt(g('CAN_GIAO')) + '</td><td class="n">' + fmt(g('MOI')) + '</td>' +
      '<td class="n">' + fmt(g('XONG')) + '</td><td class="n">' + fmt(g('HUY')) + '</td>' +
      '<td class="n"><b>' + fmt(tc.byChannel[c]) + '</b></td></tr>');
  });
  $('#tblChan').innerHTML = rows.join('');

  ds = ds || { duplicates: [], unknownStatuses: {}, noBarcode: [] };
  const uk = Object.keys(ds.unknownStatuses || {});
  // thiếu mã vạch: gộp từ file vừa nạp + đơn đang xử lý trên máy chủ chưa gán mã
  const bcLive = [];
  Object.values(S.orders).forEach((o) => {
    if (o.statusGroup !== 'CAN_GIAO' && o.statusGroup !== 'MOI') return;
    (o.items || []).forEach((it) => { if (!it.isSub && !it.barcode) bcLive.push({ orderId: o.orderId, name: it.name }); });
  });
  WARN = {
    dup: { n: (ds.duplicates || []).length, lv: '', label: 'đơn trùng giữa file', head: 'Đã tự xử lý: giữ bản ở file mới nhất, bỏ bản cũ. Không cần làm gì.', items: (ds.duplicates || []).map((d) => esc(d.orderId) + ' — giữ bản trong ' + esc(d.giuFile)) },
    bc: { n: bcLive.length, lv: 'hot', label: 'dòng thiếu mã vạch', head: 'Sàn không gắn SKU. Ở bước Pick phải gán mã vạch tay cho những dòng này.', items: bcLive.map((d) => esc(d.orderId) + ' — ' + esc(d.name)) },
    st: { n: uk.length, lv: 'mid', label: 'trạng thái lạ', head: 'Sàn dùng trạng thái app chưa biết, tạm xếp vào nhóm riêng. Báo lại để bổ sung quy tắc.', items: uk.map((k) => esc(k) + ' · ' + ds.unknownStatuses[k] + ' đơn') }
  };
  const extra = [];
  newHint();
  if (s.returnFlag) extra.push('<div class="note w"><span class="ic">↩</span><div><b>' + s.returnFlag + ' đơn có yêu cầu trả hàng/hoàn tiền</b> — sẽ hiện cảnh báo khi pick.</div></div>');
  const btns = ['dup', 'bc', 'st'].map((k) => { const x = WARN[k]; return '<button data-w="' + k + '" class="' + (x.n ? x.lv : 'zero') + '"><b>' + fmt(x.n) + '</b>' + x.label + '</button>'; }).join('');
  $('#warnBox').innerHTML = '<div class="warncount">' + btns + '</div><div class="warndetail" id="warnDetail"></div>' + extra.join('');
  $('#warnBox').querySelectorAll('.warncount button').forEach((b) => { b.onclick = function () { showWarn(this.dataset.w); }; });
}
function showWarn(k) {
  const x = WARN && WARN[k], box = $('#warnDetail');
  if (!x || !x.n) return;
  if (box.dataset.open === k) { box.innerHTML = ''; box.dataset.open = ''; return; }
  box.dataset.open = k;
  const cap = x.items.slice(0, 200);
  box.innerHTML = '<div class="note ' + (x.lv === 'hot' ? 'e' : (x.lv === 'mid' ? 'w' : 'i')) + '"><span class="ic">≡</span><div><b>' +
    fmt(x.n) + ' ' + x.label + '</b> — ' + x.head + '<ul>' + cap.map((t) => '<li>' + t + '</li>').join('') +
    (x.items.length > cap.length ? '<li>… và ' + fmt(x.items.length - cap.length) + ' dòng nữa</li>' : '') + '</ul></div></div>';
}
function segColor(g) { return { CAN_GIAO: 'var(--ok)', MOI: 'var(--info)', XONG: '#4B5563', HUY: '#7A3038', KHAC: 'var(--warn)' }[g]; }

function newHint() {
  const box = $('#newHint'); if (!box) return;
  const ids = [];
  Object.values(S.orders).forEach((o) => { if (o.statusGroup === 'MOI' && !o.tracking) ids.push(o.orderId + ' (' + o.channel + ')'); });
  if (!ids.length) { box.innerHTML = ''; return; }
  box.innerHTML = '<div class="note e" style="margin-top:12px"><span class="ic">!</span><div><b>' + ids.length +
    ' đơn chờ giao chưa có mã vận đơn</b> — trên sàn chưa bấm <b>Chuẩn bị hàng</b>, nên không quét được bằng mã vận đơn. ' +
    'Bấm Chuẩn bị hàng rồi xuất lại file, hoặc quét <b>mã đơn hàng</b> trên phiếu để pick tạm.<ul>' +
    ids.slice(0, 12).map((x) => '<li>' + esc(x) + '</li>').join('') + (ids.length > 12 ? '<li>… và ' + (ids.length - 12) + ' đơn nữa</li>' : '') + '</ul></div></div>';
}

export function readyUI() {
  const ready = hasAny(); const nf = newFiles();
  $('#btnGoPick').style.display = ready ? '' : 'none';
  $('#rowSetup').style.display = (T.files.length || ready) ? '' : 'none';
  if (ready) {
    $('#runHint').innerHTML = nf.length
      ? '<span style="color:var(--warn)"><b>' + nf.length + ' file Excel mới chưa nạp</b> — bấm Xử lý dữ liệu để gộp vào máy chủ.</span>'
      : (T.files.length ? 'Mọi file đã được nạp. Bấm Xử lý dữ liệu khi có file mới.' : 'Chọn thư mục hoặc file Excel mới để nạp thêm.');
  } else {
    $('#runHint').textContent = nf.length ? 'Bấm Xử lý dữ liệu để đọc ' + nf.length + ' file và gửi lên máy chủ.' : '';
  }
  newHint();
}

/* Sau khi tải đầu ca: hộp tóm tắt + popup sẵn sàng (một lần) */
export async function afterLoad() {
  const tc = totalCounts(); const s = S.sum;
  let p = 0, o = 0;
  Object.values(S.orders).forEach((x) => { if (x.pickState === 'done') p++; if (x.outState === 'done') o++; });
  if (!hasAny()) {
    $('#sessBox').innerHTML = '<div class="note i"><span class="ic">i</span><div>Máy chủ chưa có đơn nào. Chọn file Excel rồi bấm <b>Xử lý dữ liệu</b>.</div></div>';
    return;
  }
  $('#sessBox').innerHTML = '<div class="note ' + (p + o ? 's' : 'i') + '"><span class="ic">' + (p + o ? '▶' : 'i') + '</span><div>' +
    '<b>Đã tải dữ liệu từ máy chủ</b> — máy <b>' + esc(S.may) + '</b>.<br><span class="small">' +
    fmt(tc.total) + ' đơn · ' + fmt(s.byGroup.CAN_GIAO) + ' cần giao · đã pick ' + p + ' · đã out ' + o + '</span></div></div>';
  render(null); readyUI();
  if (T.readyShown) return; T.readyShown = true;
  const nf = newFiles(); const btns = [];
  if (nf.length) btns.push({ label: 'Nạp file mới rồi Pick', val: 'merge', cls: 'pri' });
  btns.push({ label: 'Bắt đầu Pick ngay', val: 'pick', cls: nf.length ? '' : 'pri' });
  btns.push({ label: 'Ở lại tab này', val: 'stay' });
  const act = await dialog({
    title: 'App đã sẵn sàng — ' + S.may,
    body: 'Máy chủ có <b>' + fmt(tc.total) + ' đơn</b> · ' + fmt(s.byGroup.CAN_GIAO) + ' cần giao · đã pick ' + p + ' · đã out ' + o + '.' +
      (nf.length ? '<br><span style="color:var(--warn)">⚠ Có <b>' + nf.length + ' file Excel mới chưa nạp</b>.</span>' : ''),
    buttons: btns
  });
  if (act === 'merge') { await run(); T.goPick(); }
  else if (act === 'pick') T.goPick();
}
export function onData(fn) { T.onData = fn; }
